import { cloneDeep, sum } from 'lodash';
import { IFramework } from '@/framework';
import {
    DEFAULT_TABLE_CELL__BACKGROUND_COLOR,
    DEFAULT_TABLE_CELL__LINE_HEIGHT,
    DEFAULT_TABLE_CELL__TEXT_ALIGN,
    DEFAULT_TABLE_CELL__TEXT_COLOR,
    DEFAULT_TABLE_CELL__TEXT_FONT_SIZE,
    DEFAULT_TABLE_CELL__VERTICAL_ALIGN,
    TABLE_CELL_HORIZONTAL_PADDING,
} from '@/layers/table-layer/default-configs';
import { getCellStyle, getFixedHsizes, getUpdatedTableStyle } from '@/layers/table-layer/utils/table';
import { getTdElementId } from '@/layers/table-layer/view/cell-component';
import { getTableEditState } from '@/plugins/table-edit/state';
import addOrUpdateLayer from '@/plugins/table-edit/utils/add-or-update-layer';
import { COL_ELEMENT_IS_BLOCK } from '@/utils/common-kit';
import { ILayerProps, ITableLayer } from '@/version-types';
import { DEFAULT_FONT_FAMILY } from '@/version-types/font';
import { TextEditDependency } from '../dependency';
import { getTextEditState } from '../states/text-edit-state';
import { type ClipboardData } from '../types';
import { cleanAndUnescapeHtml, insertPlainText } from '../utils';
import type { ITextEditAdapter } from './interface';

const getRenderCell = (frameworkIdentity: string) => {
    const activeCell = getTableEditState(frameworkIdentity).activeCell;
    return activeCell
        ? document.querySelector<HTMLElement>(`#${frameworkIdentity} #${getTdElementId(activeCell)}`)
        : null;
};

/**
 * 文字编辑逻辑:
 * - TableLayer中的文字进入编辑态时, 单独维护一套histories, 用于undo/redo文字的同时, 更新TableLayer
 * - 从文字编辑开始到文字编辑完成后, TableLayer是一次完整的更新, 这次更新会在文字编辑后推送到editor的history
 */
export class TableLayerAdapter implements ITextEditAdapter {
    layerId: string | null = null;
    activeCellId: string | null = null;
    histories: ITableLayer[] = [];
    currentHistoryIndex = 0;
    tempTableEl: HTMLTableElement | null = null;
    renderCell: HTMLElement | null = null;

    useRendererDisplay() {
        return false;
    }

    resetStates = () => {
        this.layerId = null;
        this.activeCellId = null;
        this.histories = [];
        this.currentHistoryIndex = 0;
        this.tempTableEl?.remove();
        this.tempTableEl = null;
        this.renderCell = null;
    };

    getActiveCell = (layer: ITableLayer) => {
        return layer.data.cells.flat().find((c) => c?.__renderId === this.activeCellId) ?? null;
    };

    onBeforeEnterEditing: ITextEditAdapter['onBeforeEnterEditing'] = (framework, originLayer: ITableLayer) => {
        this.resetStates();

        const renderCell = getRenderCell(framework.identity);
        this.renderCell = renderCell;
        renderCell && (renderCell.style.opacity = '0');

        this.layerId = originLayer.id;
        const { activeCell } = getTableEditState(framework.identity);
        const activeCellId = activeCell
            ? (originLayer.data.cells[activeCell.vIndex]?.[activeCell.hIndex]?.__renderId ?? null)
            : null;
        this.activeCellId = activeCellId;
        return originLayer;
    };

    onAfterEnterEditing: ITextEditAdapter['onAfterEnterEditing'] = (_, originLayer: ITableLayer) => {
        this.histories = [originLayer];
        return originLayer;
    };

    onExitEditing: ITextEditAdapter['onExitEditing'] = async (
        framework,
        originLayer: ITableLayer,
        latestLayer: ITableLayer | null,
    ) => {
        const renderCell = this.renderCell;
        renderCell && (renderCell.style.opacity = '1');

        if (!latestLayer) return;
        const originalText = this.getActiveCell(originLayer)?.content ?? null;
        const latestText = this.getActiveCell(latestLayer)?.content ?? null;
        if (originalText === latestText) return;

        const { setState } = getTableEditState(framework.identity);
        framework.history.write(cloneDeep(originLayer), cloneDeep(latestLayer));
        setState({ hasEditingText: false, baseCellsBeforeEditing: null });

        this.resetStates();

        return latestLayer;
    };

    onUpdate: ITextEditAdapter['onUpdate'] = (framework, editArea, preLayer: ITableLayer, options) => {
        const activeCell = this.getActiveCell(preLayer);
        if (!activeCell) return preLayer;

        const newText = cleanAndUnescapeHtml(editArea.innerHTML);

        const updateBaseCells = (in_newLayer: ITableLayer): ITableLayer => {
            let delta = 0;

            const newInfo = getUpdatedTableStyle({
                frameworkIdentity: framework.identity,
                tableLayerId: in_newLayer.id,
                tableRotate: in_newLayer.rotate,
                preserveTempElement: true,
                action: (el, { updateTextContentOrAttr }) => {
                    if (!this.tempTableEl) {
                        this.tempTableEl = el;
                    }

                    const originalBaseCells = getTableEditState(framework.identity).baseCellsBeforeEditing;
                    const shrinkToCellWidth = originalBaseCells
                        ? sum(
                              originalBaseCells.horizontal.slice(
                                  activeCell.hIndex,
                                  activeCell.hIndex + activeCell.colSpan,
                              ),
                          )
                        : undefined;

                    delta = updateTextContentOrAttr({
                        currentTableLayerData: in_newLayer.data,
                        fontFamilyMap: in_newLayer.__runtime?.fontFamilyMap ?? {},
                        cellInfo: {
                            id: activeCell.__renderId,
                            hIndex: activeCell.hIndex,
                            vIndex: activeCell.vIndex,
                        },
                        newContent: newText,
                        shrinkToCellWidth,
                    }).delta;
                },
                compute: (_, { getLayout }) =>
                    getLayout({
                        tableWidth: preLayer.width,
                        tableHeight: preLayer.height,
                        horizontalSizes: preLayer.data.baseCells.horizontal,
                        verticalSizes: preLayer.data.baseCells.vertical,
                    }),
            });
            if (newInfo) {
                in_newLayer.height = newInfo.tableHeight;
                in_newLayer.data.baseCells.vertical = newInfo.baseCells.vertical;
                if (COL_ELEMENT_IS_BLOCK) {
                    in_newLayer.width = newInfo.tableWidth;
                    in_newLayer.data.baseCells.horizontal = newInfo.baseCells.horizontal;
                } else {
                    in_newLayer.data.baseCells.horizontal = getFixedHsizes(in_newLayer);
                    in_newLayer.width += delta;
                    in_newLayer.data.baseCells.horizontal[activeCell.hIndex] += delta;
                    return preLayer;
                }
            }
            return in_newLayer;
        };

        if (options?.inputType && ['historyUndo', 'historyRedo'].includes(options.inputType)) {
            this.currentHistoryIndex += options.inputType === 'historyUndo' ? -1 : 1;
            const targetHistory = this.histories[this.currentHistoryIndex];
            if (!targetHistory) return preLayer;
            let newLayer = cloneDeep(targetHistory);

            updateBaseCells(newLayer);

            framework.stage.addOrUpdate(newLayer);
            newLayer = addOrUpdateLayer({
                frameworkIdentity: framework.identity,
                layer: newLayer,
                resetMaterialIds: true,
            });
            framework.operate.setActiveLayer(newLayer.id);
            return newLayer;
        }

        let newLayer = cloneDeep(preLayer);

        // 1. 文字属性修改
        this.getActiveCell(newLayer)!.content = newText;

        // 2. 修改文字后, 获取最新宽高布局
        updateBaseCells(newLayer);

        // 4. 更新图层
        framework.stage.addOrUpdate(newLayer);
        newLayer = addOrUpdateLayer({
            frameworkIdentity: framework.identity,
            layer: newLayer,
            resetMaterialIds: true,
        });
        framework.operate.setActiveLayer(newLayer.id);

        // 5. 更新历史
        this.histories = [...this.histories.slice(0, this.currentHistoryIndex + 1), cloneDeep(newLayer)];
        this.currentHistoryIndex++;

        return newLayer;
    };

    onCopyOrCut = (framework: IFramework, layer: ITableLayer) => {
        const cell = this.getActiveCell(layer);
        if (!cell || !cell.content) return;

        framework.getInstance(TextEditDependency).setPlainTextToClipboard(cell.content);
    };

    onPaste = (framework: IFramework, layer: ILayerProps, data: ClipboardData) => {
        const { text } = data;
        insertPlainText(text);
    };

    onBlur = (framework: IFramework, layer: ILayerProps) => {
        const originLayer = getTextEditState(framework.identity).getOriginLayer();
        if (!originLayer) return;

        const originActiveCell = this.getActiveCell(originLayer as ITableLayer);
        const currentActiveCell = this.getActiveCell(layer as ITableLayer);

        if (!originActiveCell || !currentActiveCell) return;

        const originText = originActiveCell.content;
        const currentText = currentActiveCell.content;

        if (currentText !== originText) {
            framework.history.write(originLayer, layer);
        }
    };

    /** 对应dom: #j-text-edit-area */
    getEditAreaStyle: ITextEditAdapter['getEditAreaStyle'] = (framework, layer: ITableLayer) => {
        const cell = this.getActiveCell(layer);
        if (!cell) return {};
        const style = getCellStyle(cell, layer.data);
        const {
            disableAutoWrap = false,
            bold,
            italic,
            underline,
            lineThrough,
            color = DEFAULT_TABLE_CELL__TEXT_COLOR,
            fontFamily = DEFAULT_FONT_FAMILY().name,
            fontSize = DEFAULT_TABLE_CELL__TEXT_FONT_SIZE,
            textAlign = DEFAULT_TABLE_CELL__TEXT_ALIGN,
            verticalAlign = DEFAULT_TABLE_CELL__VERTICAL_ALIGN,
            backgroundColor = DEFAULT_TABLE_CELL__BACKGROUND_COLOR,
        } = style;
        const resultStyle: React.CSSProperties = {
            // 基础样式
            whiteSpace: 'break-spaces',
            wordBreak: 'break-all',

            // 水平/竖直对齐样式
            paddingLeft: TABLE_CELL_HORIZONTAL_PADDING,
            paddingRight: TABLE_CELL_HORIZONTAL_PADDING,
            display: 'flex',
            width: disableAutoWrap
                ? // 加max-content是为了修复bug: 非自动换行，且文字内容超出单元格时的编辑区样式问题 (宽度需要由内容撑开)
                  'max-content'
                : '100%',
            // fix issue: https://jira.meitu.com/browse/MTXXPRO-54336 (非自动换行+水平剧中的问题)
            minWidth: '100%',
            height: '100%',
            justifyContent: {
                left: 'left',
                center: 'center',
                right: 'right',
            }[textAlign],
            alignItems: {
                top: 'flex-start',
                center: 'center',
                bottom: 'flex-end',
            }[verticalAlign],

            // 自定义样式
            fontFamily,
            fontSize,
            textAlign,
            lineHeight: DEFAULT_TABLE_CELL__LINE_HEIGHT,
            color,
            fontWeight: bold ? 'bold' : 'normal',
            fontStyle: italic ? 'italic' : 'normal',
            backgroundColor,
            textDecoration: underline ? 'underline' : lineThrough ? 'line-through' : 'none',
        };
        return resultStyle;
    };

    getEditAreaTextContent: ITextEditAdapter['getEditAreaTextContent'] = (framework, layer: ITableLayer) => {
        const cell = this.getActiveCell(layer);
        if (!cell) return { uniformText: '' };
        return {
            uniformText: cell.content,
        };
    };

    /**
     * 获取编辑区域包装样式
     *
     * - 对应 DOM: `#j-text-edit-area`的 `parentNode`
     *
     * @param framework - 框架实例对象
     * @param layer - 表格图层属性
     */
    getEditAreaWrapperStyle: ITextEditAdapter['getEditAreaWrapperStyle'] = (framework, layer: ITableLayer) => {
        const cell = this.getActiveCell(layer);
        if (!cell) return {};

        const {
            baseCells: { horizontal: horizontalSizes, vertical: verticalSizes },
            cells: cellsProp,
        } = layer.data;

        const style = getCellStyle(cell, layer.data);

        const firstCellStyle = getCellStyle(cellsProp[0][0]!, layer.data);

        const tableFakeBorderTop = (firstCellStyle.border?.top?.width ?? 0) / 2;
        const tableFakeBorderLeft = (firstCellStyle.border?.left?.width ?? 0) / 2;
        const cellWidth = sum(horizontalSizes.slice(cell.hIndex, cell.hIndex + cell.colSpan));
        const cellHeight = sum(verticalSizes.slice(cell.vIndex, cell.vIndex + cell.rowSpan));
        const cellTop = sum(verticalSizes.slice(0, cell.vIndex));
        const cellLeft = sum(horizontalSizes.slice(0, cell.hIndex));
        const cellBorder = {
            top: style.border?.top?.width ?? 0,
            left: style.border?.left?.width ?? 0,
            right: style.border?.right?.width ?? 0,
            bottom: style.border?.bottom?.width ?? 0,
        };
        /** 旋转前的top */
        const top = tableFakeBorderTop + cellTop;
        /** 旋转前的left */
        const left = tableFakeBorderLeft + cellLeft;

        return {
            position: 'absolute',
            top,
            left,
            width: cellWidth,
            height: cellHeight,
            borderWidth: `${cellBorder.top / 2}px ${cellBorder.right / 2}px ${
                cellBorder.bottom / 2
            }px ${cellBorder.left / 2}px`,
            borderStyle: 'solid',
            borderColor: 'transparent',
            opacity: layer.opacity,
        };
    };

    getEnterEditingModeWays: ITextEditAdapter['getEnterEditingModeWays'] = () => {
        return ['click', 'doubleClick'];
    };
}
